import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Tab from "@mui/material/Tab";
import TablePagination from "@mui/material/TablePagination";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Logger from "../../../services/Logger";
import ConfirmDialog from "../../components/ConfirmDialog";
import CustomSnackbar from "../../components/CustomSnackbar";
import BannedService from "../../setting/banned_words/BannedService";
import { PUBLISHED } from "../board/AdminWordWheelBoardService";
import { isValidCrosswordLayout } from "../board/wordWheelScatterLayout";
import {
    extractSeasonFromRow,
    isJourneyPack,
} from "../wordWheelLevelUtils";
import AdminWordWheelService from "../tpl/AdminWordWheelService";
import WordWheelPublishDialog from "../tpl/WordWheelPublishDialog";
import WordWheelTplGridCard from "../tpl/WordWheelTplGridCard";
import AdminWordWheelPackService from "./AdminWordWheelPackService";
import AdminJourneyMapMain from "../journey/AdminJourneyMapMain";
import AdminJourneyMapService from "../journey/AdminJourneyMapService";

const PACK_TAB_STORAGE_KEY = "admin.word_wheel_pack.tab";
const PACK_NAV_CODE_STORAGE_KEY = "admin.word_wheel_pack.nav.code";
const PACK_NAV_STATE_STORAGE_KEY = "admin.word_wheel_pack.nav.state";
const PACK_NAV_PAGE_STORAGE_KEY = "admin.word_wheel_pack.nav.page";
const PACK_NAV_PAGE_SIZE_STORAGE_KEY = "admin.word_wheel_pack.nav.pageSize";
const PACK_NAV_SORT_STORAGE_KEY = "admin.word_wheel_pack.nav.sort";
const PACK_TABS = ["season", "packs", "navigator", "journey"];
const NAV_STATES = [
    { value: "all", label: "All" },
    { value: "DRAFT", label: "Draft" },
    { value: "READY_TO_REVIEW", label: "Ready to review" },
    { value: "REVIEWED", label: "Reviewed" },
    { value: "PUBLISHED", label: "Published" },
];
const NAV_PAGE_SIZES = [15, 25, 50, 100];
const NAV_SORT_OPTIONS = [
    { value: "puzzleLevel:asc", label: "Level (low → high)" },
    { value: "publishedAt:desc", label: "Published at (newest)" },
    { value: "publishedAt:asc", label: "Published at (oldest)" },
    { value: "createdAt:desc", label: "Created at (newest)" },
    { value: "createdAt:asc", label: "Created at (oldest)" },
];
const NAV_INACTIVE_STATES = new Set(["DISABLED", "DISCARDED", "ARCHIVED"]);

function isNavigatorPackMember(row) {
    const state = String(row?.state || "").toUpperCase();
    return !NAV_INACTIVE_STATES.has(state);
}

function loadStoredPackTab() {
    try {
        const stored = localStorage.getItem(PACK_TAB_STORAGE_KEY);
        if (PACK_TABS.includes(stored)) {
            return stored;
        }
    } catch (error) {
        Logger.error("Error reading word wheel pack tab from localStorage:", error);
    }
    return "season";
}

function loadStoredNavCode() {
    try {
        const stored = localStorage.getItem(PACK_NAV_CODE_STORAGE_KEY);
        if (stored && /^[a-z0-9_]+$/i.test(stored)) {
            return stored;
        }
    } catch (error) {
        Logger.error("Error reading word wheel pack navigator pack from localStorage:", error);
    }
    return "classic";
}

function loadStoredNavState() {
    try {
        const stored = localStorage.getItem(PACK_NAV_STATE_STORAGE_KEY);
        if (NAV_STATES.some((option) => option.value === stored)) {
            return stored;
        }
    } catch (error) {
        Logger.error("Error reading word wheel pack navigator state from localStorage:", error);
    }
    return "all";
}

function loadStoredNavPageSize() {
    try {
        const stored = Number(localStorage.getItem(PACK_NAV_PAGE_SIZE_STORAGE_KEY));
        if (NAV_PAGE_SIZES.includes(stored)) {
            return stored;
        }
    } catch (error) {
        Logger.error("Error reading word wheel pack navigator page size from localStorage:", error);
    }
    return 15;
}

function loadStoredNavPage() {
    try {
        const stored = Number(localStorage.getItem(PACK_NAV_PAGE_STORAGE_KEY));
        if (Number.isInteger(stored) && stored >= 0) {
            return stored;
        }
    } catch (error) {
        Logger.error("Error reading word wheel pack navigator page from localStorage:", error);
    }
    return 0;
}

function loadStoredNavSort() {
    try {
        const stored = localStorage.getItem(PACK_NAV_SORT_STORAGE_KEY);
        if (NAV_SORT_OPTIONS.some((option) => option.value === stored)) {
            return stored;
        }
    } catch (error) {
        Logger.error("Error reading word wheel pack navigator sort from localStorage:", error);
    }
    return "puzzleLevel:asc";
}

function navSortToModel(value) {
    const [field, dir] = String(value || "").split(":");
    if (!field || (dir !== "asc" && dir !== "desc")) {
        return [{ field: "puzzleLevel", sort: "asc" }];
    }
    return [{ field, sort: dir }];
}

const emptyForm = {
    name: "",
    code: "",
    maxLevel: "200",
    minLetters: "3",
};

function resultMessage(result, fallback) {
    if (result?.message) {
        return result.message;
    }
    if (result?.shortfall > 0) {
        return `${result.puzzleCount} of ${result.maxLevel}. ${result.shortfall} still needed.`;
    }
    return fallback;
}

function isSeasonPack(pack) {
    const code = String(pack?.code || "");
    return isJourneyPack(code) || code.startsWith("season_");
}

const BLOCKED_BANNED_CATEGORIES = new Set([
    "profanity",
    "violence",
    "drugs",
    "offensive",
    "inappropriate",
    "other",
]);

function puzzleWords(row) {
    return String(row?.wordsInUse || "")
        .split(/[\n,;]+/)
        .map((word) => word.trim())
        .filter(Boolean);
}

function bannedKeySet(list) {
    const keys = new Set();
    for (const banned of Array.isArray(list) ? list : []) {
        const category = String(banned?.category || "").trim().toLowerCase();
        if (!BLOCKED_BANNED_CATEGORIES.has(category)) {
            continue;
        }
        if (banned?.word) {
            keys.add(String(banned.word).trim().toLowerCase());
        }
        if (banned?.wordNorm) {
            keys.add(String(banned.wordNorm).trim().toLowerCase());
        }
    }
    return keys;
}

function minWordsForPack(pack) {
    return pack?.code === "hard_quest" ? 5 : 1;
}

function puzzleIsValid(row, pack, bannedKeys) {
    const words = puzzleWords(row);
    if (words.length < minWordsForPack(pack)) {
        return false;
    }
    const min = pack?.minLetters ?? 3;
    const max = pack?.maxLetters ?? 8;
    const wordsOk = words.every((word) => {
        const length = word.length;
        return length >= min && length <= max && !bannedKeys.has(word.toLowerCase());
    });
    if (!wordsOk) {
        return false;
    }
    return isValidCrosswordLayout(row?.filledCoordinates, words);
}

const JOURNEY_PACK_CODES = new Set(["classic", "hard_quest", "master"]);

function canMakeJourney(pack) {
    const code = String(pack?.code || "").toLowerCase();
    if (isJourneyPack(code) || code.startsWith("season_")) {
        return false;
    }
    return JOURNEY_PACK_CODES.has(code) || Number(pack?.maxLevel) > 0;
}

function chapterSizeHint(maxLevel) {
    const n = Number(maxLevel) || 0;
    if (n > 0 && n <= 500 && n % 25 === 0) {
        return 25;
    }
    if (n > 0 && n % 50 === 0) {
        return 50;
    }
    return 25;
}

function PackCard({ pack, busyCode, onAction, onLockChange, onMakeJourney, hasJourney }) {
    const isJourney = isJourneyPack(pack.code) || String(pack?.code || "").startsWith("season_");
    const locked = Boolean(pack.locked) || isJourney;
    const busy = busyCode === pack.code
        || busyCode === `lock:${pack.code}`
        || busyCode === `journey:${pack.code}`;
    const showMakeJourney = canMakeJourney(pack);
    const sizeHint = chapterSizeHint(pack.maxLevel);
    const chapterCount = pack.maxLevel > 0 && sizeHint > 0 ? Math.floor(pack.maxLevel / sizeHint) : 0;

    return (
        <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, justifyContent: "space-between" }}>
                <Typography sx={{ fontWeight: 600 }}>
                    {pack.name} ({pack.code}) — {pack.puzzleCount} / {pack.maxLevel}, {pack.minLetters}–{pack.maxLetters} letters
                    {pack.code === "hard_quest" ? ", over 4 words" : ""}
                </Typography>
                <FormControlLabel
                    control={(
                        <Checkbox
                            checked={locked}
                            disabled={busy || isJourney}
                            onChange={(event) => onLockChange?.(pack.code, event.target.checked)}
                        />
                    )}
                    label="Lock"
                    sx={{ ml: 0, mr: 0 }}
                />
            </Box>
            {locked ? (
                <Alert severity="info" sx={{ mt: 1 }}>
                    {isJourney
                        ? "Season 2026 is view only. Generate, re-order, clues, and dedupe stay disabled."
                        : "Pack is locked. Generate, re-order levels, clue tools, and remove duplicates are disabled."}
                </Alert>
            ) : (
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}>
                    <Button
                        variant="contained"
                        disabled={busy}
                        onClick={() => onAction(pack.code, AdminWordWheelPackService.generate, "Pack generated")}
                        sx={{ minHeight: 44 }}
                    >
                        Generate the pack
                    </Button>
                    <Button
                        variant="outlined"
                        disabled={busy}
                        onClick={() => onAction(pack.code, AdminWordWheelPackService.reorder, "Levels re-ordered")}
                        sx={{ minHeight: 44 }}
                    >
                        Re-order levels
                    </Button>
                    <Button
                        variant="outlined"
                        disabled={busy}
                        onClick={() => onAction(pack.code, AdminWordWheelPackService.regenerateClues, "Clues regenerated")}
                        sx={{ minHeight: 44 }}
                    >
                        Re-generate clues
                    </Button>
                    <Button
                        variant="outlined"
                        disabled={busy}
                        onClick={() => onAction(pack.code, AdminWordWheelPackService.fillMissingClues, "Missing clues filled")}
                        sx={{ minHeight: 44 }}
                    >
                        Generate missing clues
                    </Button>
                    <Button
                        variant="outlined"
                        color="error"
                        disabled={busy}
                        onClick={() => onAction(pack.code, AdminWordWheelPackService.removeDuplicates, "Duplicate puzzles removed")}
                        sx={{ minHeight: 44 }}
                    >
                        Remove duplicate puzzles
                    </Button>
                </Box>
            )}
            {showMakeJourney && (
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1, alignItems: "center" }}>
                    <Button
                        variant="contained"
                        color="secondary"
                        disabled={busy || Number(pack.puzzleCount) < Number(pack.maxLevel)}
                        onClick={() => onMakeJourney?.(pack)}
                        sx={{ minHeight: 44 }}
                    >
                        {hasJourney ? "Remake journey" : "Make journey"}
                    </Button>
                    <Typography variant="body2" color="text.secondary">
                        → track <code>ww_{pack.code}</code>
                        {chapterCount > 0
                            ? ` · ${chapterCount} chapters × ${sizeHint} (categories from this pack’s titles)`
                            : ""}
                        {Number(pack.puzzleCount) < Number(pack.maxLevel)
                            ? " · fill all levels first"
                            : ""}
                    </Typography>
                </Box>
            )}
        </Paper>
    );
}

export default function AdminWordWheelPackMain() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [tab, setTab] = useState(loadStoredPackTab);
    const [packs, setPacks] = useState([]);
    const [journeyTrackIds, setJourneyTrackIds] = useState(() => new Set());
    const [loading, setLoading] = useState(true);
    const [busyCode, setBusyCode] = useState("");
    const [form, setForm] = useState(emptyForm);
    const [navCode, setNavCode] = useState(loadStoredNavCode);
    const [navState, setNavState] = useState(loadStoredNavState);
    const [navRows, setNavRows] = useState([]);
    const [navRowCount, setNavRowCount] = useState(0);
    const [navPage, setNavPage] = useState(loadStoredNavPage);
    const [navPageSize, setNavPageSize] = useState(loadStoredNavPageSize);
    const [navSort, setNavSort] = useState(loadStoredNavSort);
    const [navCountKnown, setNavCountKnown] = useState(false);
    const [navLoading, setNavLoading] = useState(false);
    const [bannedKeys, setBannedKeys] = useState(null);
    const [publishTpl, setPublishTpl] = useState(null);
    const [journeyConfirmPack, setJourneyConfirmPack] = useState(null);
    const [publishLoading, setPublishLoading] = useState(false);
    const [snack, setSnack] = useState({ open: false, severity: "success", message: "" });

    const notify = (severity, message) => setSnack({ open: true, severity, message });

    const seasonPacks = packs.filter(isSeasonPack);
    const contentPacks = packs.filter((pack) => !isSeasonPack(pack));

    const loadPacks = useCallback(async () => {
        try {
            const items = await AdminWordWheelPackService.list();
            setPacks(items);
            setNavCode((current) => (
                items.some((pack) => pack.code === current) ? current : (items[0]?.code || "")
            ));
        } catch (error) {
            Logger.error("Error loading word wheel packs:", error);
            notify("error", "Could not load packs. Run the pack SQL, then restart the backend.");
        } finally {
            setLoading(false);
        }
    }, []);

    const loadJourneyTracks = useCallback(async () => {
        try {
            const tracks = await AdminJourneyMapService.listTracks();
            const ids = new Set((Array.isArray(tracks) ? tracks : []).map((track) => track.trackId).filter(Boolean));
            setJourneyTrackIds(ids);
        } catch (error) {
            Logger.warn("Journey tracks unavailable:", error);
        }
    }, []);

    useEffect(() => {
        loadPacks();
        loadJourneyTracks();
    }, [loadPacks, loadJourneyTracks]);

    useEffect(() => {
        if (searchParams.get("tab") !== "journey") {
            return;
        }
        setTab("journey");
        setSearchParams({}, { replace: true });
    }, [searchParams, setSearchParams]);

    useEffect(() => {
        try {
            localStorage.setItem(PACK_TAB_STORAGE_KEY, tab);
        } catch (error) {
            Logger.error("Error saving word wheel pack tab to localStorage:", error);
        }
    }, [tab]);

    useEffect(() => {
        try {
            localStorage.setItem(PACK_NAV_CODE_STORAGE_KEY, navCode);
        } catch (error) {
            Logger.error("Error saving word wheel pack navigator pack to localStorage:", error);
        }
    }, [navCode]);

    useEffect(() => {
        try {
            localStorage.setItem(PACK_NAV_STATE_STORAGE_KEY, navState);
        } catch (error) {
            Logger.error("Error saving word wheel pack navigator state to localStorage:", error);
        }
    }, [navState]);

    useEffect(() => {
        try {
            localStorage.setItem(PACK_NAV_PAGE_SIZE_STORAGE_KEY, String(navPageSize));
        } catch (error) {
            Logger.error("Error saving word wheel pack navigator page size to localStorage:", error);
        }
    }, [navPageSize]);

    useEffect(() => {
        try {
            localStorage.setItem(PACK_NAV_PAGE_STORAGE_KEY, String(navPage));
        } catch (error) {
            Logger.error("Error saving word wheel pack navigator page to localStorage:", error);
        }
    }, [navPage]);

    useEffect(() => {
        try {
            localStorage.setItem(PACK_NAV_SORT_STORAGE_KEY, navSort);
        } catch (error) {
            Logger.error("Error saving word wheel pack navigator sort to localStorage:", error);
        }
    }, [navSort]);

    useEffect(() => {
        if (!navCountKnown) {
            return;
        }
        const maxPage = Math.max(0, Math.ceil(navRowCount / navPageSize) - 1);
        if (navPage > maxPage) {
            setNavPage(maxPage);
        }
    }, [navRowCount, navPageSize, navPage, navCountKnown]);

    const runPackAction = async (code, action, successText) => {
        setBusyCode(code);
        try {
            const result = await action(code);
            notify("success", resultMessage(result, successText));
            await loadPacks();
        } catch (error) {
            Logger.error("Word wheel pack action failed:", error);
            notify("error", error?.message || "Pack action failed");
        } finally {
            setBusyCode("");
        }
    };

    const handleLockChange = async (code, locked) => {
        setBusyCode(`lock:${code}`);
        try {
            const result = await AdminWordWheelPackService.setLocked(code, locked);
            notify("success", resultMessage(result, locked ? "Pack locked" : "Pack unlocked"));
            await loadPacks();
        } catch (error) {
            Logger.error("Word wheel pack lock failed:", error);
            notify("error", error?.message || "Could not update pack lock");
        } finally {
            setBusyCode("");
        }
    };

    const handleMakeJourney = (pack) => {
        if (!pack?.code) {
            return;
        }
        setJourneyConfirmPack(pack);
    };

    const confirmMakeJourney = async (code) => {
        if (!code) {
            return;
        }
        const trackId = `ww_${code}`;
        const exists = journeyTrackIds.has(trackId);
        setBusyCode(`journey:${code}`);
        try {
            const result = await AdminWordWheelPackService.makeJourney(code, true);
            if (result?.skipped) {
                notify("warning", result.reason || "Journey already exists");
            } else {
                notify("success", resultMessage(result, exists ? `Journey map remade: ${trackId}` : `Journey map created: ${trackId}`));
                setJourneyTrackIds((prev) => {
                    const next = new Set(prev);
                    next.add(trackId);
                    return next;
                });
            }
        } catch (error) {
            Logger.error("Make journey failed:", error);
            notify("error", error?.message || "Make journey failed");
        } finally {
            setBusyCode("");
        }
    };

    const handleCreate = async () => {
        setBusyCode("create");
        try {
            await AdminWordWheelPackService.create({
                name: form.name,
                code: form.code,
                maxLevel: Number(form.maxLevel),
                minLetters: Number(form.minLetters),
                maxLetters: 8,
            });
            setForm(emptyForm);
            notify("success", "Pack created");
            await loadPacks();
        } catch (error) {
            Logger.error("Error creating word wheel pack:", error);
            notify("error", error?.message || "Could not create pack");
        } finally {
            setBusyCode("");
        }
    };

    useEffect(() => {
        let cancelled = false;
        const loadBanned = async () => {
            try {
                const list = await BannedService.requestList("english");
                if (!cancelled) {
                    setBannedKeys(bannedKeySet(list));
                }
            } catch (error) {
                Logger.error("Error loading banned words for pack navigator:", error);
                if (!cancelled) {
                    setBannedKeys(new Set());
                }
            }
        };
        loadBanned();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (tab !== "navigator" || !navCode) {
            return undefined;
        }
        let cancelled = false;
        const loadGrid = async () => {
            setNavLoading(true);
            try {
                await AdminWordWheelService.requestList(
                    { page: navPage, pageSize: navPageSize },
                    navSortToModel(navSort),
                    (rows) => {
                        if (!cancelled) {
                            const list = Array.isArray(rows) ? rows : [];
                            setNavRows(list.filter(isNavigatorPackMember));
                        }
                    },
                    (count) => {
                        if (!cancelled) {
                            setNavRowCount(Number(count) || 0);
                            setNavCountKnown(true);
                        }
                    },
                    "",
                    navState === "all" ? null : navState,
                    "all",
                    navCode,
                    { includeGrid: true },
                );
            } catch (error) {
                Logger.error("Error loading pack navigator grid:", error);
                if (!cancelled) {
                    setNavRows([]);
                    setNavRowCount(0);
                    setNavCountKnown(true);
                    notify("error", error?.message || "Could not load this pack");
                }
            } finally {
                if (!cancelled) {
                    setNavLoading(false);
                }
            }
        };
        loadGrid();
        return () => {
            cancelled = true;
        };
    }, [tab, navCode, navState, navPage, navPageSize, navSort]);

    const handlePublishConfirm = async (options) => {
        if (!publishTpl?.id) {
            return;
        }
        setPublishLoading(true);
        try {
            const existingSeason = extractSeasonFromRow(publishTpl);
            const season = options.season
                || (existingSeason && !isJourneyPack(existingSeason) ? existingSeason : "");
            const result = await AdminWordWheelService.promoteTemplatesToPublished(
                [publishTpl],
                { ...options, season },
            );
            if (result.failed.length > 0) {
                notify("error", result.failed[0]?.error || "Failed to publish puzzle.");
                return;
            }
            if (result.skipped.length > 0 && result.succeeded.length === 0) {
                notify("info", "This puzzle is already published.");
                setPublishTpl(null);
                return;
            }
            AdminWordWheelService.clearPublishedBySeasonCache();
            setNavRows((rows) => rows.map((item) => (
                item.id === publishTpl.id
                    ? { ...item, state: PUBLISHED, season: season || item.season }
                    : item
            )));
            notify("success", "Puzzle published.");
            setPublishTpl(null);
        } catch (error) {
            Logger.error("Error publishing pack navigator puzzle:", error);
            notify("error", error?.message || "Failed to publish puzzle.");
        } finally {
            setPublishLoading(false);
        }
    };

    const handleTabChange = (_event, value) => {
        if (PACK_TABS.includes(value)) {
            setTab(value);
        }
    };

    const selectedPack = packs.find((pack) => pack.code === navCode);

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
                Word Wheel Packs
            </Typography>
            <Tabs
                value={tab}
                onChange={handleTabChange}
                sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
            >
                <Tab value="season" label="Season" sx={{ minHeight: 44 }} />
                <Tab value="packs" label="Packs" sx={{ minHeight: 44 }} />
                <Tab value="navigator" label="Navigator" sx={{ minHeight: 44 }} />
                <Tab value="journey" label="Journey map" sx={{ minHeight: 44 }} />
            </Tabs>

            {tab === "season" && (
                loading ? <CircularProgress /> : (
                    seasonPacks.length > 0 ? seasonPacks.map((pack) => (
                        <PackCard
                            key={pack.code}
                            pack={pack}
                            busyCode={busyCode}
                            onAction={runPackAction}
                            onLockChange={handleLockChange}
                            onMakeJourney={handleMakeJourney}
                            hasJourney={journeyTrackIds.has(`ww_${pack.code}`)}
                        />
                    )) : (
                        <Alert severity="info">No season pack is set up yet.</Alert>
                    )
                )
            )}

            {tab === "packs" && (
                <>
                    <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
                        <Typography sx={{ mb: 1 }}>New pack</Typography>
                        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                            <TextField
                                size="small"
                                label="Name"
                                value={form.name}
                                onChange={(event) => setForm({ ...form, name: event.target.value })}
                            />
                            <TextField
                                size="small"
                                label="Code"
                                value={form.code}
                                onChange={(event) => setForm({ ...form, code: event.target.value })}
                            />
                            <TextField
                                size="small"
                                label="Max level"
                                type="number"
                                value={form.maxLevel}
                                onChange={(event) => setForm({ ...form, maxLevel: event.target.value })}
                                sx={{ width: 120 }}
                            />
                            <TextField
                                size="small"
                                label="Min letters"
                                type="number"
                                value={form.minLetters}
                                onChange={(event) => setForm({ ...form, minLetters: event.target.value })}
                                sx={{ width: 120 }}
                            />
                            <Button
                                variant="contained"
                                onClick={handleCreate}
                                disabled={busyCode === "create"}
                                sx={{ minHeight: 44 }}
                            >
                                Create pack
                            </Button>
                        </Box>
                    </Paper>
                    {loading ? <CircularProgress /> : contentPacks.map((pack) => (
                        <PackCard
                            key={pack.code}
                            pack={pack}
                            busyCode={busyCode}
                            onAction={runPackAction}
                            onLockChange={handleLockChange}
                            onMakeJourney={handleMakeJourney}
                            hasJourney={journeyTrackIds.has(`ww_${pack.code}`)}
                        />
                    ))}
                </>
            )}

            {tab === "navigator" && (
                <Box>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center", mb: 2 }}>
                        <FormControl size="small" sx={{ minWidth: 220 }}>
                            <InputLabel>Pack</InputLabel>
                            <Select
                                label="Pack"
                                value={packs.some((pack) => pack.code === navCode) ? navCode : ""}
                                onChange={(event) => {
                                    setNavCode(event.target.value);
                                    setNavPage(0);
                                }}
                            >
                                {packs.map((pack) => (
                                    <MenuItem key={pack.code} value={pack.code}>{pack.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 180 }}>
                            <InputLabel>Status</InputLabel>
                            <Select
                                label="Status"
                                value={navState}
                                onChange={(event) => {
                                    setNavState(event.target.value);
                                    setNavPage(0);
                                }}
                            >
                                {NAV_STATES.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 220 }}>
                            <InputLabel>Sort</InputLabel>
                            <Select
                                label="Sort"
                                value={navSort}
                                onChange={(event) => {
                                    setNavSort(event.target.value);
                                    setNavPage(0);
                                }}
                            >
                                {NAV_SORT_OPTIONS.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                    {navLoading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                            <CircularProgress size={32} />
                        </Box>
                    ) : navRows.length === 0 ? (
                        <Paper sx={{ py: 4, textAlign: "center" }}>
                            <Typography color="text.secondary">No puzzles in this pack.</Typography>
                        </Paper>
                    ) : (
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 220px), 1fr))",
                                gap: 2,
                            }}
                        >
                            {navRows.map((row) => (
                                <WordWheelTplGridCard
                                    key={row.id}
                                    row={row}
                                    hideSelect
                                    levelText
                                    wordCount={puzzleWords(row).length}
                                    valid={bannedKeys ? puzzleIsValid(row, selectedPack, bannedKeys) : undefined}
                                    onOpen={(tpl) => navigate(`/admin/puzzle/word_wheel_tpl/detail/${tpl.id}`, {
                                        state: { backTo: "/admin/puzzle/word_wheel_pack" },
                                    })}
                                    onLayoutSaved={(id, saved) => {
                                        setNavRows((rows) => rows.map((item) => (
                                            item.id === id ? { ...item, filledCoordinates: saved } : item
                                        )));
                                    }}
                                    onNotify={(severity, message) => notify(severity, message)}
                                    onPublish={(tpl) => setPublishTpl(tpl)}
                                    publishing={publishLoading && publishTpl?.id === row.id}
                                />
                            ))}
                        </Box>
                    )}
                    <TablePagination
                        component="div"
                        count={navRowCount}
                        page={navPage}
                        onPageChange={(_event, page) => setNavPage(page)}
                        rowsPerPage={navPageSize}
                        onRowsPerPageChange={(event) => {
                            setNavPage(0);
                            setNavPageSize(Number(event.target.value));
                        }}
                        rowsPerPageOptions={NAV_PAGE_SIZES}
                    />
                </Box>
            )}

            {tab === "journey" && <AdminJourneyMapMain embedded />}

            <ConfirmDialog
                open={Boolean(journeyConfirmPack)}
                setOpen={(open) => {
                    if (!open) {
                        setJourneyConfirmPack(null);
                    }
                }}
                title={journeyTrackIds.has(`ww_${journeyConfirmPack?.code}`) ? "Remake journey" : "Make journey"}
                targetName={journeyConfirmPack ? `${journeyConfirmPack.name} (${journeyConfirmPack.code})` : ""}
                message={
                    journeyTrackIds.has(`ww_${journeyConfirmPack?.code}`)
                        ? `This replaces the existing ww_${journeyConfirmPack?.code} chapters and levels.`
                        : `This builds ww_${journeyConfirmPack?.code} from this pack’s level titles.`
                }
                warningAlert={
                    journeyTrackIds.has(`ww_${journeyConfirmPack?.code}`)
                        ? "Chapter images already generated for this track are removed."
                        : ""
                }
                targetId={journeyConfirmPack?.code}
                okText={journeyTrackIds.has(`ww_${journeyConfirmPack?.code}`) ? "Remake" : "Make"}
                cancelText="Cancel"
                callback={confirmMakeJourney}
            />
            <WordWheelPublishDialog
                open={Boolean(publishTpl)}
                onClose={() => !publishLoading && setPublishTpl(null)}
                onConfirm={handlePublishConfirm}
                templates={publishTpl ? [publishTpl] : []}
                language={publishTpl?.language || "english"}
                loading={publishLoading}
            />
            <CustomSnackbar
                open={snack.open}
                setOpen={(open) => setSnack((current) => ({ ...current, open }))}
                severity={snack.severity}
                message={snack.message}
            />
        </Box>
    );
}
